import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { TextField } from "@components/Input/Input.styles";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.port-services";
const EXTERNAL_ATTACHMENT_UPLOAD_KEY = "portServicesExternalAttachment";

const ExternalNotesAndAttachmentsComponent = () => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const {
    setExternalNotes,
    externalNotes,
    errors,
    setErrors,
    externalOnlySchema,
    setExternalAttachment,
  } = usePortServicesFlow();

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const TEXTAREA_MAX_CHARS = 1000;
  const externalUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const externalFiles = useUploadStore(
    (s) => s.filesByKey[EXTERNAL_ATTACHMENT_UPLOAD_KEY] ?? EMPTY_UPLOAD_ENTRIES,
  );

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = fileProfiles.sideDrawerApprovalFile;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setExternalAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(EXTERNAL_ATTACHMENT_UPLOAD_KEY);
        return;
      }
      setFilesForKey(EXTERNAL_ATTACHMENT_UPLOAD_KEY, files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeExternalFile = () => {
    clearFilesForKey(EXTERNAL_ATTACHMENT_UPLOAD_KEY);
    setExternalAttachment({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          setFilesForKey(EXTERNAL_ATTACHMENT_UPLOAD_KEY, [
            {
              name: file.name,
              url: documentUrl,
              fileType: file.type,
            },
          ]);
          setExternalAttachment({
            documentUrl: documentUrl,
            documentName: file?.name,
            fileType: file?.type,
          });
          return { documentUrl, documentType };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (value: {
      internalFile?: string;
      externalFile?: string;
      files?: string;
    }) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ externalFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors({ files: undefined });
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      externalUploadSignatureRef.current = null;
      removeExternalFile();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2" data-testid="port-services-external-notes">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.external-notes`)}
      </Typography>
      <TextField
        multiline
        minRows={4}
        value={externalNotes}
        inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
        placeholder={t(`${LANG_BASE}.fields.external-notes`)}
        sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!isPrintableKey(e)) return;
          if (externalNotes.length >= TEXTAREA_MAX_CHARS) {
            setErrors({
              ...errors,
              externalNotes: t("form-validation.max-length", {
                label: t(`${LANG_BASE}.fields.external-notes`),
                max: TEXTAREA_MAX_CHARS,
              }),
            });
          }
        }}
        onBlur={() => {
          const res = externalOnlySchema.safeParse({ externalNotes });
          if (!res.success) {
            const flat = res.error.flatten().fieldErrors;
            setErrors({ ...errors, externalNotes: flat.externalNotes?.[0] });
          } else {
            setErrors({ ...errors, externalNotes: undefined });
          }
        }}
        onChange={(e) => {
          const val = e.target.value;
          setExternalNotes(val);
          const res = externalOnlySchema.safeParse({ externalNotes: val });
          if (!res.success) {
            const flat = res.error.flatten().fieldErrors;
            setErrors({ ...errors, externalNotes: flat.externalNotes?.[0] });
          } else {
            setErrors({ ...errors, externalNotes: undefined });
          }
        }}
      />
      {errors.externalNotes && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.externalNotes}</FormHelperText>
      )}
      <FormHelperText sx={charCounterSx}>
        {externalNotes.length}/{TEXTAREA_MAX_CHARS}
      </FormHelperText>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.external-attachments`)}
      </Typography>
      <DocumentUpload
        testId="port-services-external-attachment-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
              }),
            setUploadFiles: setExternalAttachmentFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "EXTERNAL_NOTES"),
            uploadTracker: externalUploadSignatureRef,
          })
        }
        name={EXTERNAL_ATTACHMENT_UPLOAD_KEY}
        conditions={internalProfile}
        files={externalFiles}
        setFiles={(next) => setFilesForKey(EXTERNAL_ATTACHMENT_UPLOAD_KEY, next)}
        maxFiles={1}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={t("upload.dragOrDropDescription")}
      />
      {errors.externalFile && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.externalFile}</FormHelperText>
      )}
      {externalFiles[0] && (
        <SingleFile
          file={externalFiles[0]}
          testId="port-services-external-attachment"
          fieldName={EXTERNAL_ATTACHMENT_UPLOAD_KEY}
          currentFileIndex={0}
          setFiles={() => {
            setErrors({ ...errors, files: undefined });
            setExternalAttachmentFiles([]);
            externalUploadSignatureRef.current = null;
          }}
          removeFn={removeExternalFile}
        />
      )}
    </div>
  );
};

export default ExternalNotesAndAttachmentsComponent;
